import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Facebook, Instagram, Menu, Phone, X } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { ThemeToggle } from './ThemeToggle';
import { CONTACT, SOCIAL } from '../constants/site';
import { WhatsAppIcon } from './WhatsAppFloat';
import { EASE } from './motion';

// Rolling-text hover micro-effect: label slides up, accent duplicate rolls in from below
const RollText = ({ text }) => (
  <span className="relative block overflow-hidden leading-tight">
    <span className="block transition-transform duration-300 ease-out group-hover/roll:-translate-y-full">{text}</span>
    <span aria-hidden="true" className="absolute inset-0 block translate-y-full transition-transform duration-300 ease-out group-hover/roll:translate-y-0 text-accent">
      {text}
    </span>
  </span>
);

// Magnetic CTA: gently follows the cursor, sheen sweeps across on hover
const MagneticCta = ({ to, testId, children }) => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setOffset({
      x: ((e.clientX - rect.left) / rect.width - 0.5) * 12,
      y: ((e.clientY - rect.top) / rect.height - 0.5) * 10,
    });
  };

  return (
    <motion.div
      animate={{ x: offset.x, y: offset.y }}
      transition={{ type: 'spring', stiffness: 180, damping: 14, mass: 0.6 }}
      className="inline-block"
    >
      <Link
        to={to}
        data-testid={testId}
        data-track="click_quote"
        onMouseMove={onMove}
        onMouseLeave={() => setOffset({ x: 0, y: 0 })}
        className="group/cta relative inline-flex items-center gap-2.5 overflow-hidden bg-accent text-white text-[11px] font-bold uppercase tracking-[0.18em] pl-7 pr-6 py-3.5 rounded-full shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_30px_rgba(200,16,46,0.35)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_14px_40px_rgba(200,16,46,0.45)] transition-shadow duration-300"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -translate-x-[130%] skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-out group-hover/cta:translate-x-[130%]"
        />
        <span className="relative">{children}</span>
        <ArrowRight size={14} className="relative transition-transform duration-300 group-hover/cta:translate-x-1" />
      </Link>
    </motion.div>
  );
};

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
    <img src="/logo.png" alt="Domidoor Solutions" className="h-12 w-auto logo-img logo-img-light" />
    <img src="/logo-white.png" alt="Domidoor Solutions" className="h-12 w-auto logo-img logo-img-dark" />
  </Link>
);

const LanguageSwitcher = ({ tone = 'default' }) => {
  const { lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find((l) => l.code === lang);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const btnTone = tone === 'navy'
    ? 'text-white/75 hover:text-white'
    : 'text-smoke hover:text-ink';

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 text-[10px] uppercase tracking-widest transition-colors px-1.5 py-1.5 ${btnTone}`}
      >
        <img src={`https://flagcdn.com/w40/${current.flag}.png`} alt={current.code} className="w-4 h-auto rounded-[2px]" />
        <span>{current.code}</span>
        <ChevronDown size={12} className={`transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.ul
            data-testid="language-dropdown"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 top-full mt-2 w-44 bg-surface border border-line/10 shadow-2xl z-50"
          >
            {languages.map((l) => (
              <li key={l.code}>
                <button
                  data-testid={`language-option-${l.code}`}
                  onClick={() => { setLang(l.code); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-ink/5 ${l.code === lang ? 'text-accent' : 'text-ink'}`}
                >
                  <img src={`https://flagcdn.com/w40/${l.flag}.png`} alt={l.code} className="w-5 h-auto rounded-[2px]" />
                  {l.label}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setMobileOpen(false), [location.pathname]);

  // hide the sticky action bar while the fullscreen menu is open
  useEffect(() => {
    document.body.classList.toggle('menu-open', mobileOpen);
    return () => document.body.classList.remove('menu-open');
  }, [mobileOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', label: t('nav.home'), testId: 'nav-link-home' },
    { to: '/tamplarie-pvc', label: t('nav.pvc'), testId: 'nav-link-pvc' },
    { to: '/rulouri', label: t('nav.rulouri'), testId: 'nav-link-rulouri' },
    { to: '/porti-de-garaj', label: t('nav.garaj'), testId: 'nav-link-garaj' },
    { to: '/automatizari', label: t('nav.auto'), testId: 'nav-link-automatizari' },
    { to: '/portofoliu', label: t('nav.portofoliu'), testId: 'nav-link-portofoliu' },
    { to: '/contact', label: t('nav.contact'), testId: 'nav-link-contact' },
  ];
  const serviceLinks = links.slice(1, 5);

  return (
    <>
      {/* Tier 1 — Floating navy island (desktop only, slides away on scroll) */}
      <div
        data-testid="nav-minibar"
        className={`hidden lg:flex fixed top-4 right-8 z-[60] h-10 px-6 items-center gap-5 rounded-full bg-[#0A142F] text-white/90 border border-white/10 shadow-[0_8px_32px_rgba(10,20,47,0.25)] backdrop-blur-md transition-all duration-500 ${
          scrolled ? '-translate-y-[200%] opacity-0' : 'translate-y-0 opacity-100'
        }`}
      >
        <span aria-hidden="true" className="font-display italic text-lg leading-none text-accent select-none -mr-2">S</span>
        <NavLink
          to="/portofoliu"
          data-testid="mini-nav-portofoliu"
          className={({ isActive }) =>
            `group/roll text-[10px] uppercase tracking-[0.22em] transition-colors hover:text-white ${isActive ? 'text-accent' : 'text-white/75'}`
          }
        >
          <RollText text={t('nav.portofoliu')} />
        </NavLink>
        <NavLink
          to="/contact"
          data-testid="mini-nav-contact"
          className={({ isActive }) =>
            `group/roll text-[10px] uppercase tracking-[0.22em] transition-colors hover:text-white ${isActive ? 'text-accent' : 'text-white/75'}`
          }
        >
          <RollText text={t('nav.contact')} />
        </NavLink>
        <span className="w-px h-3.5 bg-white/15" />
        <div className="flex items-center gap-3.5">
          <a href={SOCIAL.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="mini-social-facebook" className="text-white/60 hover:text-accent hover:-translate-y-0.5 transition-[color,transform] duration-300">
            <Facebook size={13} strokeWidth={1.8} />
          </a>
          <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="mini-social-instagram" className="text-white/60 hover:text-accent hover:-translate-y-0.5 transition-[color,transform] duration-300">
            <Instagram size={13} strokeWidth={1.8} />
          </a>
        </div>
        <span className="w-px h-3.5 bg-white/15" />
        <ThemeToggle className="text-white/70 hover:text-accent" />
        <LanguageSwitcher tone="navy" />
      </div>

      {/* Tier 2 — Floating glass card */}
      <header
        data-testid="navbar"
        className={`fixed z-50 transition-all duration-500 ${
          scrolled ? 'top-4' : 'top-16 max-lg:top-4'
        } inset-x-4 lg:inset-x-8 max-w-7xl mx-auto h-20 rounded-2xl force-light bg-paper/85 backdrop-blur-xl border border-black/5 shadow-[0_12px_40px_rgba(24,26,32,0.10)]`}
      >
        <div className="h-full px-6 flex items-center justify-between gap-6">
          <Logo />
          <nav className="hidden lg:flex items-center gap-6">
            {serviceLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `group/roll text-[11px] font-medium uppercase tracking-[0.18em] transition-colors hover:text-ink ${
                    isActive ? 'text-accent' : 'text-ink'
                  }`
                }
              >
                <RollText text={l.label} />
              </NavLink>
            ))}
            <NavLink
              to="/configurator"
              data-testid="nav-link-configurator"
              className="inline-flex items-center gap-2 border border-accent/50 text-accent text-[11px] font-bold uppercase tracking-[0.18em] px-4 py-2 rounded-full hover:bg-accent hover:text-white transition-colors duration-300"
            >
              <span className="dd-blink w-1.5 h-1.5 rounded-full bg-current" />
              {t('configurator.nav')}
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3 lg:hidden">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
            <div className="hidden md:block">
              <MagneticCta to="/contact" testId="nav-cta-button">{t('nav.cta')}</MagneticCta>
            </div>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden text-ink p-2"
              aria-label="Menu"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-paper lg:hidden overflow-y-auto"
          >
            <div
              className="min-h-full flex flex-col pt-24 px-8"
              style={{ paddingBottom: 'calc(1.75rem + env(safe-area-inset-bottom))' }}
            >
              <nav className="flex flex-col">
                {links.map((l, i) => (
                  <motion.div key={l.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.testId}`}
                      className={({ isActive }) =>
                        `font-display text-xl font-medium py-2.5 block border-b border-line/5 ${isActive ? 'text-accent' : 'text-ink'}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: links.length * 0.05 }}>
                  <NavLink
                    to="/configurator"
                    data-testid="mobile-nav-link-configurator"
                    className="dd-blink font-display text-xl font-medium py-2.5 inline-flex items-center gap-2 text-accent"
                  >
                    {t('configurator.nav')} →
                  </NavLink>
                </motion.div>
              </nav>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.5, ease: EASE }}
                className="mt-auto pt-6 flex flex-col gap-3"
              >
                <Link
                  to="/contact"
                  data-testid="mobile-menu-quote-button"
                  data-track="click_quote"
                  className="flex items-center justify-center gap-3 bg-accent text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-3.5 rounded-full active:bg-accenth transition-colors"
                >
                  {t('nav.cta')} <ArrowRight size={16} />
                </Link>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="mobile-menu-call-button"
                    className="flex items-center justify-center gap-2.5 border border-line/20 text-ink font-bold text-xs uppercase tracking-[0.12em] px-4 py-3.5 rounded-full active:bg-ink/5 transition-colors"
                  >
                    <Phone size={15} className="text-accent" /> {t('mobile.call')}
                  </a>
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mobile-menu-whatsapp-button"
                    className="flex items-center justify-center gap-2.5 border border-[#25D366]/40 text-[#1DA851] font-bold text-xs uppercase tracking-[0.12em] px-4 py-3.5 rounded-full active:bg-[#25D366]/10 transition-colors"
                  >
                    <WhatsAppIcon size={15} /> WhatsApp
                  </a>
                </div>
                <div className="flex items-center justify-center gap-4 pt-1">
                  <a
                    href={SOCIAL.facebook}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Facebook"
                    data-testid="mobile-menu-social-facebook"
                    className="w-10 h-10 grid place-items-center rounded-full border border-line/15 text-smoke active:text-accent active:border-accent/50 transition-colors"
                  >
                    <Facebook size={16} strokeWidth={1.8} />
                  </a>
                  <a
                    href={SOCIAL.instagram}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    data-testid="mobile-menu-social-instagram"
                    className="w-10 h-10 grid place-items-center rounded-full border border-line/15 text-smoke active:text-accent active:border-accent/50 transition-colors"
                  >
                    <Instagram size={16} strokeWidth={1.8} />
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
